import { AnimatePresence } from 'framer-motion';
import { DateTime } from 'luxon';
import { FC, useState } from 'react';
import { useSelector } from 'react-redux';
import styled, { css } from 'styled-components';

import { ReactComponent as Pencil } from '@/assets/icons/pencil-with-background.svg';
import { ReactComponent as PencilIcon } from '@/assets/icons/pencil.svg';
import ActionsDropdown, {
  ActionsDropdownButton,
} from '@/components/ActionsDropdown';
import RestrictedContent from '@/components/RestrictedContent';
import ScalableThumbnail from '@/components/ScalableThumbnail';
import { ActionsWrap, Td, Topics, TopicsList } from '@/components/Table';
import Text from '@/components/Text';
import TopicTag from '@/components/TopicTag';
import AttachmentModal from '@/pages/AssignTopics/components/AttachmentModal';
import { TopVideosResource } from '@/types/resources';
import { durationFormatter, numFormatter } from '@/utils/helpers';
import { RootState } from '@/utils/store';
import { Color, hexToRgba } from '@/utils/theme';

const StyledActionsDropdown = styled(ActionsDropdown)`
  width: 192px;
`;

const NoWrap = styled.span`
  white-space: nowrap;
`;

const TopicsTd = styled(Td)`
  min-width: 200px;
`;

const AIComment = styled.div`
  width: 330px;
  display: flex;
  align-items: flex-start;
`;

const AICommentDot = styled.div<{ $color: Color }>`
  flex-shrink: 0;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin-right: 6px;
  position: relative;

  ${({ $color, theme }) => css`
    background: ${hexToRgba(theme.colors[$color], 0.1)};
    &::before {
      width: 6px;
      height: 6px;
      content: '';
      position: absolute;
      left: 0;
      right: 0;
      top: 0;
      border-radius: 50%;
      bottom: 0;
      margin: auto;
      background: ${theme.colors[$color]};
    }
  `}
`;

const EYTVStatTd = styled(Td)`
  white-space: pre-line;
`;

export interface AttachmentModalState {
  isOpen: boolean;
  category: string;
}

interface Props {
  video: TopVideosResource;
  optionsKey: string;
  videosQueryKey: Array<any>;
  areThumbsEnlarged: boolean;
  setAreThumbsEnlarged: React.Dispatch<React.SetStateAction<boolean>>;
}

const TopVideosTableRow: FC<Props> = ({
  video,
  optionsKey,
  videosQueryKey,
  areThumbsEnlarged,
  setAreThumbsEnlarged,
}) => {
  const tableViewOptions = useSelector(
    (rootState: RootState) => rootState.options.tableViews[optionsKey],
  );
  const [attachmentModalState, setAttachmentModalState] =
    useState<AttachmentModalState>({ isOpen: false, category: '' });

  const scriptTopics = video.topics?.filter(
    (topic) => topic.category === 'script',
  );

  const thumbTopics = video.topics?.filter(
    (topic) => topic.category === 'thumb',
  );

  const titleTopics = video.topics?.filter(
    (topic) => topic.category === 'title',
  );

  const typeTopics = video.topics?.filter((topic) => topic.category === 'type');

  return (
    <tr>
      <Td>
        <ScalableThumbnail
          areThumbsEnlarged={areThumbsEnlarged}
          setAreThumbsEnlarged={setAreThumbsEnlarged}
          thumbnail={video.thumbnail}
        />
      </Td>
      <Td>
        <Text
          as="a"
          href={video.url}
          target="_blank"
          rel="noopener noreferrer"
          $type="smallBold"
        >
          {video.title}
        </Text>
        <br />
        <Text
          href={video.channelUrl}
          as="a"
          target="_blank"
          $type="extraSmall"
          $color="gray"
          rel="noopener noreferrer"
        >
          {video.channelName}
        </Text>
      </Td>
      {tableViewOptions['AI Comment Sum (Positive)'] && (
        <Td>
          {video.commentsSummary?.positive ? (
            <AIComment>
              <AICommentDot $color="success" />
              {video.commentsSummary.positive}
            </AIComment>
          ) : (
            '-'
          )}
        </Td>
      )}
      {tableViewOptions['AI Comment Sum (Negative)'] && (
        <Td>
          {video.commentsSummary?.negative ? (
            <AIComment>
              <AICommentDot $color="error" />
              {video.commentsSummary.negative}
            </AIComment>
          ) : (
            '-'
          )}
        </Td>
      )}
      {tableViewOptions['AI Comment Sum (New Ideas)'] && (
        <Td>
          {video.commentsSummary?.ideas ? (
            <AIComment>
              <AICommentDot $color="production" />
              {video.commentsSummary.ideas}
            </AIComment>
          ) : (
            '-'
          )}
        </Td>
      )}
      {tableViewOptions.Published && (
        <Td>
          <NoWrap>{DateTime.fromISO(video.publishedAt).toRelative()}</NoWrap>
          <br />
          <Text $type="extraSmall" $color="gray" as="span">
            <NoWrap>
              {DateTime.fromISO(video.publishedAt).toLocaleString()}
            </NoWrap>
          </Text>
        </Td>
      )}
      {tableViewOptions.Views && (
        <Td>
          <NoWrap>{video.views ? numFormatter(video.views) : '-'}</NoWrap>
        </Td>
      )}
      {tableViewOptions.Duration && (
        <Td>
          <NoWrap>
            {video.duration ? durationFormatter(video.duration) : '-'}
          </NoWrap>
        </Td>
      )}
      {tableViewOptions.V7 && (
        <Td>
          <NoWrap>{video.v7 ? numFormatter(video.v7) : '-'}</NoWrap>
        </Td>
      )}
      {tableViewOptions.V30 && (
        <Td>
          <NoWrap>{video.v30 ? numFormatter(video.v30) : '-'}</NoWrap>
        </Td>
      )}
      {tableViewOptions.VPH7 && (
        <Td>
          <NoWrap>{video.vph7 ? numFormatter(video.vph7) : '-'}</NoWrap>
        </Td>
      )}
      {tableViewOptions.VPH30 && (
        <Td>
          <NoWrap>{video.vph30 ? numFormatter(video.vph30) : '-'}</NoWrap>
        </Td>
      )}
      {tableViewOptions['V7 Index'] && (
        <Td>
          <NoWrap>{video.v7Index?.toFixed(2) ?? '-'}</NoWrap>
        </Td>
      )}
      {tableViewOptions['V30 Index'] && (
        <Td>
          <NoWrap>{video.v30Index?.toFixed(2) ?? '-'}</NoWrap>
        </Td>
      )}
      {tableViewOptions.Revenue && (
        <Td>
          <RestrictedContent>
            <NoWrap>
              {video.revenue ? `$ ${numFormatter(video.revenue)}` : '-'}
            </NoWrap>
          </RestrictedContent>
        </Td>
      )}
      {tableViewOptions.RPM && (
        <Td>
          <RestrictedContent>
            <NoWrap>{video.rpm !== null ? `$ ${video.rpm}` : '-'}</NoWrap>
          </RestrictedContent>
        </Td>
      )}
      {tableViewOptions.R7 && (
        <Td>
          <RestrictedContent>
            <NoWrap>{video.r7 ? `$ ${numFormatter(video.r7)}` : '-'}</NoWrap>
          </RestrictedContent>
        </Td>
      )}
      {tableViewOptions.R30 && (
        <Td>
          <RestrictedContent>
            <NoWrap>{video.r30 ? `$ ${numFormatter(video.r30)}` : '-'}</NoWrap>
          </RestrictedContent>
        </Td>
      )}
      {tableViewOptions['R7 Index'] && (
        <Td>
          <RestrictedContent>
            <NoWrap>{video.r7Index?.toFixed(2) ?? '-'}</NoWrap>
          </RestrictedContent>
        </Td>
      )}
      {tableViewOptions['R30 Index'] && (
        <Td>
          <RestrictedContent>
            <NoWrap>{video.r30Index?.toFixed(2) ?? '-'}</NoWrap>
          </RestrictedContent>
        </Td>
      )}
      {tableViewOptions['Testing Date'] && (
        <Td>
          {video.gaStats.updated_at
            ? DateTime.fromSQL(video.gaStats.updated_at).toFormat(
                'dd/LL/yyyy HH:mm',
              )
            : '-'}
        </Td>
      )}
      {tableViewOptions['eYTV (Watch)'] && (
        <Td>{video.gaStats.final_value ?? '-'}</Td>
      )}
      {tableViewOptions['eYTV 30s (Watch)'] && (
        <Td>{video.gaStats.eytv30s ?? '-'}</Td>
      )}
      {tableViewOptions['eYTV 45s (Watch)'] && (
        <Td>{video.gaStats.eytv45s ?? '-'}</Td>
      )}
      {tableViewOptions['eYTV 60s (Watch)'] && (
        <Td>{video.gaStats.eytv60s ?? '-'}</Td>
      )}
      {tableViewOptions['eYTV 80s (Watch)'] && (
        <Td>{video.gaStats.eytv80s ?? '-'}</Td>
      )}
      {tableViewOptions['eYTV 120s (Watch)'] && (
        <Td>{video.gaStats.eytv120s ?? '-'}</Td>
      )}
      {tableViewOptions['eYTV 150s (Watch)'] && (
        <Td>{video.gaStats.eytv150s ?? '-'}</Td>
      )}
      {tableViewOptions['eYTV 30m (Watch)'] && (
        <Td>{video.gaStats.eytv30m ?? '-'}</Td>
      )}
      {tableViewOptions['eYTV 60m (Watch)'] && (
        <Td>{video.gaStats.eytv60m ?? '-'}</Td>
      )}
      {tableViewOptions['e8min (Watch)'] && (
        <Td>
          {video.gaStats.milestone_views_percentage
            ? `${video.gaStats.milestone_views_percentage} %`
            : '-'}
        </Td>
      )}
      {tableViewOptions['eYTV 4min (Watch)'] && (
        <Td>{video.gaStats.eytv4 ?? '-'}</Td>
      )}
      {tableViewOptions['e60min (Watch)'] && (
        <Td>{video.gaStats.e60m ? `${video.gaStats.e60m} %` : '-'}</Td>
      )}
      {tableViewOptions['e30min (Watch)'] && (
        <Td>{video.gaStats.e30m ? `${video.gaStats.e30m} %` : '-'}</Td>
      )}
      {tableViewOptions['e4min (Watch)'] && (
        <Td>{video.gaStats.e4min ? `${video.gaStats.e4min} %` : '-'}</Td>
      )}
      {tableViewOptions['e150s (Watch)'] && (
        <Td>{video.gaStats.e150s ? `${video.gaStats.e150s} %` : '-'}</Td>
      )}
      {tableViewOptions['e120s (Watch)'] && (
        <Td>{video.gaStats.e120s ? `${video.gaStats.e120s} %` : '-'}</Td>
      )}
      {tableViewOptions['e80s (Watch)'] && (
        <Td>{video.gaStats.e80s ? `${video.gaStats.e80s} %` : '-'}</Td>
      )}
      {tableViewOptions['e60s (Watch)'] && (
        <Td>{video.gaStats.e60s ? `${video.gaStats.e60s} %` : '-'}</Td>
      )}
      {tableViewOptions['e45s (Watch)'] && (
        <Td>{video.gaStats.e45s ? `${video.gaStats.e45s} %` : '-'}</Td>
      )}
      {tableViewOptions['e30s (Watch)'] && (
        <Td>{video.gaStats.e30s ? `${video.gaStats.e30s} %` : '-'}</Td>
      )}
      {tableViewOptions['ViewRate (Watch)'] && (
        <Td>
          {video.gaStats.view_rate ? `${video.gaStats.view_rate} %` : '-'}
        </Td>
      )}
      {tableViewOptions['Views (Watch)'] && (
        <Td>{video.gaStats.views ? numFormatter(video.gaStats.views) : '-'}</Td>
      )}
      {tableViewOptions['25% View Rate (Watch)'] && (
        <Td>{video.gaStats.p25 ?? '-'}</Td>
      )}
      {tableViewOptions['50% View Rate (Watch)'] && (
        <Td>{video.gaStats.p50 ?? '-'}</Td>
      )}
      {tableViewOptions['75% View Rate (Watch)'] && (
        <Td>{video.gaStats.p75 ?? '-'}</Td>
      )}
      {tableViewOptions['100% View Rate (Watch)'] && (
        <Td>{video.gaStats.p100 ?? '-'}</Td>
      )}
      {tableViewOptions['Rec7'] && (
        <Td>
          {video.recStats.yt_rec_7
            ? numFormatter(video.recStats.yt_rec_7)
            : '-'}
        </Td>
      )}
      {tableViewOptions['Rec30'] && (
        <Td>
          {video.recStats.yt_rec_30
            ? numFormatter(video.recStats.yt_rec_30)
            : '-'}
        </Td>
      )}
      {tableViewOptions['ARP7'] && (
        <Td>{video.recStats.yt_avg_position_7 ?? '-'}</Td>
      )}
      {tableViewOptions['ARP30'] && (
        <Td>{video.recStats.yt_avg_position_30 ?? '-'}</Td>
      )}
      {tableViewOptions['Script topics'] && (
        <TopicsTd>
          <Topics $hasTopics={!!scriptTopics?.length}>
            <Pencil />
            {!!scriptTopics?.length ? (
              <TopicsList>
                {scriptTopics.map((topic) => (
                  <TopicTag topic={topic} key={topic.id} />
                ))}
              </TopicsList>
            ) : (
              <Text
                $color="gray"
                $type="small"
                as="button"
                onClick={() =>
                  setAttachmentModalState({
                    isOpen: true,
                    category: 'script',
                  })
                }
              >
                Assign topics
              </Text>
            )}
          </Topics>
        </TopicsTd>
      )}
      {tableViewOptions['Thumb topics'] && (
        <TopicsTd>
          <Topics $hasTopics={!!thumbTopics?.length}>
            <Pencil />
            {!!thumbTopics?.length ? (
              <TopicsList>
                {thumbTopics.map((topic) => (
                  <TopicTag topic={topic} key={topic.id} />
                ))}
              </TopicsList>
            ) : (
              <Text
                $color="gray"
                $type="small"
                as="button"
                onClick={() =>
                  setAttachmentModalState({
                    isOpen: true,
                    category: 'thumb',
                  })
                }
              >
                Assign topics
              </Text>
            )}
          </Topics>
        </TopicsTd>
      )}
      {tableViewOptions['Title topics'] && (
        <TopicsTd>
          <Topics $hasTopics={!!titleTopics?.length}>
            <Pencil />
            {!!titleTopics?.length ? (
              <TopicsList>
                {titleTopics.map((topic) => (
                  <TopicTag topic={topic} key={topic.id} />
                ))}
              </TopicsList>
            ) : (
              <Text
                $color="gray"
                $type="small"
                as="button"
                onClick={() =>
                  setAttachmentModalState({
                    isOpen: true,
                    category: 'title',
                  })
                }
              >
                Assign topics
              </Text>
            )}
          </Topics>
        </TopicsTd>
      )}
      {tableViewOptions['Video type'] && (
        <TopicsTd>
          <Topics $hasTopics={!!typeTopics?.length}>
            <Pencil />
            {!!typeTopics?.length ? (
              <TopicsList>
                {typeTopics.map((type) => (
                  <TopicTag topic={type} key={type.id} />
                ))}
              </TopicsList>
            ) : (
              <Text
                $color="gray"
                $type="small"
                as="button"
                onClick={() =>
                  setAttachmentModalState({
                    isOpen: true,
                    category: 'type',
                  })
                }
              >
                Assign types
              </Text>
            )}
          </Topics>
        </TopicsTd>
      )}
      {tableViewOptions['(T) File Date'] && (
        <EYTVStatTd>
          {video.eytvLinks
            .map((l) => DateTime.fromISO(l.file_date).toFormat('yyyy-LL-dd'))
            .join('\n') || '-'}
        </EYTVStatTd>
      )}
      {tableViewOptions['(T) CTR Percentage'] && (
        <EYTVStatTd>
          {video.eytvLinks
            .map((l) => l.video_view_rate_percentage)
            .join('\n') || '-'}
        </EYTVStatTd>
      )}
      {tableViewOptions['(T) P25'] && (
        <EYTVStatTd>
          {video.eytvLinks.map((l) => l.quartile_p25_rate ?? '-').join('\n') ||
            '-'}
        </EYTVStatTd>
      )}
      {tableViewOptions['(T) P50'] && (
        <EYTVStatTd>
          {video.eytvLinks.map((l) => l.quartile_p50_rate ?? '-').join('\n') ||
            '-'}
        </EYTVStatTd>
      )}
      {tableViewOptions['(T) P75'] && (
        <EYTVStatTd>
          {video.eytvLinks.map((l) => l.quartile_p75_rate ?? '-').join('\n') ||
            '-'}
        </EYTVStatTd>
      )}
      {tableViewOptions['(T) P100'] && (
        <EYTVStatTd>
          {video.eytvLinks.map((l) => l.quartile_p100_rate ?? '-').join('\n') ||
            '-'}
        </EYTVStatTd>
      )}
      {tableViewOptions['(T) Views'] && (
        <EYTVStatTd>
          {video.eytvLinks.map((l) => l.video_views ?? '-').join('\n') || '-'}
        </EYTVStatTd>
      )}
      {tableViewOptions['(T) eYTV 1m'] && (
        <EYTVStatTd>
          {video.eytvLinks.map((l) => l.eytv_1m ?? '-').join('\n') || '-'}
        </EYTVStatTd>
      )}
      {tableViewOptions['(T) eYTV 2m'] && (
        <EYTVStatTd>
          {video.eytvLinks.map((l) => l.eytv_2m ?? '-').join('\n') || '-'}
        </EYTVStatTd>
      )}
      {tableViewOptions['(T) eYTV 4m'] && (
        <EYTVStatTd>
          {video.eytvLinks.map((l) => l.eytv_4m ?? '-').join('\n') || '-'}
        </EYTVStatTd>
      )}
      {tableViewOptions['(T) eYTV 8m'] && (
        <EYTVStatTd>
          {video.eytvLinks.map((l) => l.eytv_8m ?? '-').join('\n') || '-'}
        </EYTVStatTd>
      )}
      {tableViewOptions['(T) eYTV 30m'] && (
        <EYTVStatTd>
          {video.eytvLinks.map((l) => l.eytv_30m ?? '-').join('\n') || '-'}
        </EYTVStatTd>
      )}
      {tableViewOptions['(T) e1min'] && (
        <EYTVStatTd>
          {video.eytvLinks.map((l) => l.e1m ?? '-').join('\n') || '-'}
        </EYTVStatTd>
      )}
      {tableViewOptions['(T) e2min'] && (
        <EYTVStatTd>
          {video.eytvLinks.map((l) => l.e2m ?? '-').join('\n') || '-'}
        </EYTVStatTd>
      )}
      {tableViewOptions['(T) e4min'] && (
        <EYTVStatTd>
          {video.eytvLinks.map((l) => l.e4m ?? '-').join('\n') || '-'}
        </EYTVStatTd>
      )}
      {tableViewOptions['(T) e8min'] && (
        <EYTVStatTd>
          {video.eytvLinks.map((l) => l.e8m ?? '-').join('\n') || '-'}
        </EYTVStatTd>
      )}
      {tableViewOptions['(T) e30min'] && (
        <EYTVStatTd>
          {video.eytvLinks.map((l) => l.e30m ?? '-').join('\n') || '-'}
        </EYTVStatTd>
      )}
      <Td $actionsCol>
        <ActionsWrap>
          <StyledActionsDropdown>
            <ActionsDropdownButton
              onClick={() =>
                setAttachmentModalState({
                  isOpen: true,
                  category: 'script',
                })
              }
              as="button"
            >
              <span>
                <PencilIcon />
              </span>
              {!!scriptTopics?.length
                ? 'Edit script topics'
                : 'Assign script topics'}
            </ActionsDropdownButton>
            <ActionsDropdownButton
              onClick={() =>
                setAttachmentModalState({
                  isOpen: true,
                  category: 'thumb',
                })
              }
              as="button"
            >
              <span>
                <PencilIcon />
              </span>
              {!!thumbTopics?.length
                ? 'Edit thumb topics'
                : 'Assign thumb topics'}
            </ActionsDropdownButton>
            <ActionsDropdownButton
              onClick={() =>
                setAttachmentModalState({
                  isOpen: true,
                  category: 'title',
                })
              }
              as="button"
            >
              <span>
                <PencilIcon />
              </span>
              {!!thumbTopics?.length
                ? 'Edit title topics'
                : 'Assign title topics'}
            </ActionsDropdownButton>
            <ActionsDropdownButton
              onClick={() =>
                setAttachmentModalState({
                  isOpen: true,
                  category: 'type',
                })
              }
              as="button"
            >
              <span>
                <PencilIcon />
              </span>
              {!!typeTopics?.length ? 'Edit types' : 'Assign types'}
            </ActionsDropdownButton>
          </StyledActionsDropdown>
          <AnimatePresence>
            {attachmentModalState.isOpen && (
              <AttachmentModal
                videosQueryKey={videosQueryKey}
                onClose={() =>
                  setAttachmentModalState({ isOpen: false, category: '' })
                }
                video={video}
                attachmentModalState={attachmentModalState}
              />
            )}
          </AnimatePresence>
        </ActionsWrap>
      </Td>
    </tr>
  );
};

export default TopVideosTableRow;
